import { useEffect, useState, useMemo } from 'react';
import {
  MEAL_PLAN_DAY_ORDER,
  COLD_START_PLANNER_HERO,
  isRestOfWeekDinnerDay,
  onboardingPlanLead,
  countPlannedDinners,
  onboardingPlanningIsGated,
  onboardingShouldAskToSave,
  resolveKitchenStartedAt,
  shouldPromptPastNightResolve,
} from '../../utils/mealPlanHelpers.js';
import MealRow             from './MealRow.jsx';
import EmptyDayRow         from './EmptyDayRow.jsx';
import CompactGroceryRail  from './CompactGroceryRail.jsx';
import RecipePackShelf     from './RecipePackShelf.jsx';
import PlanCookProgress, { PlanWeekStrip } from './PlanCookProgress.jsx';
import PlanWeekListHeader  from './PlanWeekListHeader.jsx';
import PlanWeekSectionHeader from './PlanWeekSectionHeader.jsx';
import { countWeekCoverage } from '../../utils/nightOff.js';
import { plannerAssistantEnabled } from '../../utils/dinnersSection.js';
import {
  allocatePreview,
  openPlanDayKeys,
  receiptLine,
  seedDraft,
  weekHasPastNight,
} from '../../utils/planWithCookPreview.js';
import MobileMoveMealSheet from './MobileMoveMealSheet.jsx';
import SwapRecipeSheet     from './SwapRecipeSheet.jsx';
import WeekNavigationBar   from './WeekNavigationBar.jsx';
import StateBanner         from '../StateBanner/StateBanner.jsx';
import SharedEmptyHero     from '../StateBanner/EmptyHero.jsx';
import PlanWithCookSheet from './PlanWithCookSheet.jsx'
import StatusPill from '../../design-system/patterns/StatusPill.jsx'
import MoveMealConflictModal from './MoveMealConflictModal.jsx'
import { useAuth } from '../../contexts/AuthContext.jsx'
import './planner.css';

const PLANNER_ASSISTANT_ENABLED = plannerAssistantEnabled()

/*
 * WeekPlanner — desktop page-level orchestrator (live meal plan API only).
 *
 * Props:
 *   plannerState:  'healthy' | 'thin-library' | 'empty-pantry' | 'cold-start'
 *   dietary:       full preferences object (meal-preference note before Suggestions)
 *   weekLabel:     string (shown in calendar strip week nav)
 *   weekNav:       optional week prev/next controls for the calendar strip
 *   livePlannerApi: API surface from usePlannerState (assigned meals, suggestions, actions)
 *   liveBanner:    optional banner for thin-library / empty-pantry
 *   libraryCount:  used for cold-start + thin-library hints
 *   starterPacks:  curated pack cards from API ([] until endpoint ships)
 */
export default function WeekPlanner({
  plannerState,
  dietary,
  weekLabel,
  weekNav = null,
  onSwitchToRecipes = null,
  onImportRecipe = null,
  libraryCount = null,
  starterPacks = [],
  addedPackIds = new Set(),
  onApplyStarterPack = null,
  livePlannerApi,
  liveBanner = null,
  onDismissLiveBanner = null,
  onViewRecipe = null,
  onSavePrompt = null,
  saveAskDismissed = false,
  highlightRecipeId = null,
  guestMode = false,
  onboardingMode = false,
  ensureAssignedIngredientLines = null,
  retryAssignedIngredientLines = null,
  assignedIngredientsLoading = false,
  assignedIngredientsError = false,
}) {
  const [groceryFilter, setGroceryFilter] = useState('missing');

  const onGroceryFilter = (next) => {
    setGroceryFilter(next)
    if (next === 'all') void ensureAssignedIngredientLines?.()
  }

  useEffect(() => {
    if (groceryFilter !== 'all' || assignedIngredientsError) return
    void ensureAssignedIngredientLines?.()
  }, [
    groceryFilter,
    assignedIngredientsError,
    ensureAssignedIngredientLines,
    livePlannerApi?.weekStartDate,
    livePlannerApi?.assignedMeals,
  ])
  const [moveMeal, setMoveMeal] = useState(null);
  const [swapMeal, setSwapMeal] = useState(null);
  const [addDayKey, setAddDayKey] = useState(null);
  const [planSheetOpen, setPlanSheetOpen] = useState(false);
  const [planSeed, setPlanSeed] = useState(null);
  const [planDayKeys, setPlanDayKeys] = useState([]);
  const { user } = useAuth();

  const rawCold = plannerState === 'cold-start';
  const isCold = rawCold && !onboardingMode;
  const displayBanner = onboardingMode ? null : (liveBanner || null);
  const highlightId = highlightRecipeId != null ? String(highlightRecipeId) : null;
  const mealIsHighlighted = (m) => Boolean(highlightId && m?.recipeId && String(m.recipeId) === highlightId);

  const packsMode = onboardingMode
    ? 'none'
    : rawCold ? 'hero' : plannerState === 'thin-library' ? 'shelf' : 'none';

  const assigned = useMemo(() => {
    return [...livePlannerApi.assignedMeals].sort(
      (a, b) => MEAL_PLAN_DAY_ORDER.indexOf(a.assignedTo) - MEAL_PLAN_DAY_ORDER.indexOf(b.assignedTo),
    );
  }, [livePlannerApi.assignedMeals]);

  const byDay = useMemo(
    () => Object.fromEntries(assigned.map(m => [m.assignedTo, m])),
    [assigned],
  );

  const stripDays = useMemo(
    () => livePlannerApi.stripDays || [],
    [livePlannerApi.stripDays],
  );

  const calCtx = livePlannerApi.calendarContext;
  const rowDayDates = calCtx?.dayDates ?? null;
  const todayKey = calCtx?.todayKey ?? null;
  const openDayKeys = useMemo(() => {
    const keys = openPlanDayKeys(byDay, stripDays);
    if (!onboardingMode) return keys;
    return keys.filter((key) => isRestOfWeekDinnerDay(key, todayKey));
  }, [byDay, onboardingMode, stripDays, todayKey]);
  const kitchenStartedAt = resolveKitchenStartedAt({
    accountCreatedAt: user?.created_at || null,
    planGeneratedAt: livePlannerApi.generatedAt || null,
    guestMode,
    guestTrial: Boolean(livePlannerApi.planMetadata?.guest_trial),
  });
  const promptPastNight = (dayKey, slotDate = null) =>
    shouldPromptPastNightResolve({
      slotDateIso: slotDate,
      weekStartIso: livePlannerApi.weekStartDate,
      dayKey,
      kitchenStartedAt,
    });

  function unassign(id) {
    const m = livePlannerApi.assignedMeals.find(x => x.id === id);
    if (m) void livePlannerApi.unassignMeal(m);
  }

  function removePick(meal) {
    if (!meal?.recipeId) return;
    void livePlannerApi.removePlannerCandidate(meal.recipeId, meal.name);
  }

  function dinnersOnWeek() {
    return countPlannedDinners(livePlannerApi.assignedMeals)
  }

  function planningIsGated() {
    return onboardingMode && onboardingPlanningIsGated(dinnersOnWeek(), saveAskDismissed)
  }

  function holdForSave() {
    if (!planningIsGated()) return false
    onSavePrompt?.()
    return true
  }

  function promptSaveIfSecondDinner(dinnersAfter) {
    if (!onboardingMode || !onboardingShouldAskToSave(dinnersAfter, saveAskDismissed)) return
    onSavePrompt?.()
  }

  function openAddDinner(dayKey) {
    if (holdForSave()) return
    const stripDay = stripDays.find((d) => d.dayKey === dayKey);
    if (stripDay?.isPast) return;
    setMoveMeal(null);
    setSwapMeal(null);
    setPlanSheetOpen(false);
    setAddDayKey(dayKey);
    // Desktop add panel is position:absolute in .planner-sheet-host; scroll to top
    // so the sheet header is in view when the user opened Add mid-page.
    const mainEl = document.querySelector('.main-content');
    if (mainEl) mainEl.scrollTop = 0;
  }

  function openSwapDinner(meal) {
    setAddDayKey(null);
    setMoveMeal(null);
    setPlanSheetOpen(false);
    setSwapMeal(meal);
    // Desktop swap panel is position:absolute in .planner-sheet-host; scroll to top
    // so the sheet header is in view when the user opened Swap mid-page.
    const mainEl = document.querySelector('.main-content');
    if (mainEl) mainEl.scrollTop = 0;
  }

  function closeRecipePicker() {
    setSwapMeal(null);
    setAddDayKey(null);
  }

  function closePlanSheet() {
    setPlanSheetOpen(false);
    requestAnimationFrame(() => {
      const adjust = document.getElementById('plan-with-cook-adjust');
      const pill = document.getElementById('plan-with-cook-pill');
      (adjust || pill)?.focus();
    });
  }

  function openPlanSheet(adjust = false) {
    if (holdForSave()) return
    setSwapMeal(null);
    setAddDayKey(null);
    setMoveMeal(null);
    const flow = livePlannerApi.planCookFlow;
    if (adjust && flow?.draft) {
      setPlanDayKeys(flow.draft.plannedDayKeys || openDayKeys);
      setPlanSeed({ ...flow.draft, replaceExisting: true });
    } else {
      setPlanDayKeys(openDayKeys);
      setPlanSeed(seedDraft(livePlannerApi.mealPlanStructure, {
        weekFull: openDayKeys.length === 0,
        openDayKeys,
        weekHasPast: weekHasPastNight(stripDays),
      }));
    }
    setPlanSheetOpen(true);
    const mainEl = document.querySelector('.main-content');
    if (mainEl) mainEl.scrollTop = 0;
  }

  async function submitPlan(draft) {
    const dinnersBefore = dinnersOnWeek()
    const [cookDays] = allocatePreview(draft, planDayKeys)
    closePlanSheet();
    const planned = await livePlannerApi.runPlanWithCook?.({
      ...draft,
      plannedDayKeys: planDayKeys,
      receipt: receiptLine(draft, planDayKeys),
    });
    if (planned) promptSaveIfSecondDinner(dinnersBefore + cookDays.length);
  }

  const heroTitle = weekNav?.heroTitle
    ? weekNav.heroTitle(isCold)
    : (isCold ? 'Almost ready to plan' : 'Plan Week');
  const assistantEnabledForWeek =
    PLANNER_ASSISTANT_ENABLED && (!weekNav || weekNav.isCurrentWeek || weekNav.isFutureWeek);

  const showPlanWithCook = onboardingMode || !guestMode
  const showLegacyStrip = !onboardingMode && (guestMode || isCold)

  return (
    <div className={`planner-page${onboardingMode ? ' planner-page--onboarding' : ''}`}>
      <div className="planner-hero">
        {!onboardingMode && (
        <div className="planner-hero-heading">
          <div className="planner-hero-title-block">
            <div className="page-intro-eyebrow-slot">
              <p className="eyebrow planner-eyebrow-tight">Plan</p>
            </div>
            <div className="h-display planner-title-lg">
              {heroTitle}
            </div>
          </div>
          {weekNav ? (
            <WeekNavigationBar
              weekLabel={weekLabel}
              canGoPrevious={weekNav.canGoPrevious}
              canGoNext={weekNav.canGoNext}
              onPreviousWeek={weekNav.goToPreviousWeek}
              onNextWeek={weekNav.goToNextWeek}
              loading={weekNav.loading}
            />
          ) : null}
        </div>
        )}
        {!isCold && livePlannerApi.notice && (
          <div className="planner-banner-row">
            <PlannerNotice
              notice={livePlannerApi.notice}
              onClear={livePlannerApi.clearNotice}
              onAction={livePlannerApi.runNoticeAction}
            />
          </div>
        )}
      </div>

      <div className={`planner-sheet-host${(swapMeal || addDayKey || planSheetOpen) ? ' is-open' : ''}`}>
      <div className={`planner-grid${isCold ? ' cold-start' : ''}${onboardingMode ? ' planner-grid--onboarding' : ''}`}>
        <div>
          {!isCold && !onboardingMode && livePlannerApi.weekStartDate && (
            <PlanCookProgress
              assigned={assigned}
              weekStartIso={livePlannerApi.weekStartDate}
              accountCreatedAt={user?.created_at || null}
              density="desktop"
              showWeekStrip={!guestMode}
              assignedByDay={byDay}
              todayKey={todayKey}
              pastDayKeys={calCtx?.pastDayKeys}
            />
          )}
          {showLegacyStrip && (
            <PlanWeekListHeader
              assignedByDay={byDay}
              dayDates={calCtx?.dayDates}
              todayKey={calCtx?.todayKey}
              density="desktop"
              dimmed={isCold}
            />
          )}

          {isCold && (
            <PlannerEmptyHero
              libraryCount={libraryCount}
              onSwitchToRecipes={onSwitchToRecipes}
              onImportRecipe={onImportRecipe}
            />
          )}

          {displayBanner && (
            <PlannerStateBanner
              banner={displayBanner}
              onDismiss={displayBanner.dismissible ? onDismissLiveBanner : null}
            />
          )}

          <div className={displayBanner ? 'planner-week-spacer-tight' : 'planner-week-spacer'}>
            {onboardingMode && (
              <div className="planner-onboarding-head">
                <PlanWeekSectionHeader
                  coverage={countWeekCoverage(byDay)}
                  canPlan={assistantEnabledForWeek}
                  busy={livePlannerApi.planCookFlow?.status === 'loading'}
                  status={livePlannerApi.planCookFlow?.status || 'idle'}
                  error={livePlannerApi.planCookFlow?.error || ''}
                  receipt={livePlannerApi.planCookFlow?.draft?.receipt || ''}
                  density="desktop"
                  onPlan={() => openPlanSheet(false)}
                  onAdjust={() => openPlanSheet(true)}
                  headingLevel={1}
                  lead={onboardingPlanLead()}
                />
              </div>
            )}
            {!isCold && (
              <div className={`planner-week-list${showPlanWithCook && !onboardingMode ? ' planner-week-list--headed' : ''}${onboardingMode ? ' planner-week-list--onboarding' : ''}`}>
                {showPlanWithCook && !onboardingMode && (
                  <PlanWeekSectionHeader
                    coverage={countWeekCoverage(byDay)}
                    canPlan={assistantEnabledForWeek}
                    busy={livePlannerApi.planCookFlow?.status === 'loading'}
                    status={livePlannerApi.planCookFlow?.status || 'idle'}
                    error={livePlannerApi.planCookFlow?.error || ''}
                    receipt={livePlannerApi.planCookFlow?.draft?.receipt || ''}
                    density="desktop"
                    onPlan={() => openPlanSheet(false)}
                    onAdjust={() => openPlanSheet(true)}
                  />
                )}
                {onboardingMode && (
                  <div className="pwl-strip">
                    <PlanWeekStrip
                      assignedByDay={byDay}
                      todayKey={todayKey}
                      pastDayKeys={calCtx?.pastDayKeys}
                      compact={false}
                    />
                  </div>
                )}
                <div
                  className={`planner-week-rows${livePlannerApi.planCookFlow?.status === 'loading' ? ' is-planning' : ''}`}
                  aria-busy={livePlannerApi.planCookFlow?.status === 'loading' || undefined}
                >
                {MEAL_PLAN_DAY_ORDER.filter((dayKey) => (
                  !onboardingMode || isRestOfWeekDinnerDay(dayKey, todayKey)
                )).map((dayKey) => {
              const m = byDay[dayKey];
              if (!m) {
                const stripDay = stripDays.find((d) => d.dayKey === dayKey);
                const isPastEmpty = Boolean(stripDay?.isPast);
                return (
                  <EmptyDayRow
                    key={dayKey}
                    dayKey={dayKey}
                    dateNum={rowDayDates?.[dayKey]}
                    isToday={todayKey != null && dayKey === todayKey}
                    density="desktop"
                    disabled={isPastEmpty}
                    status={stripDay?.status || 'planned'}
                    onAdd={() => openAddDinner(dayKey)}
                    onMarkSkipped={
                      isPastEmpty &&
                      stripDay?.planItemId &&
                      stripDay?.status !== 'skipped' &&
                      promptPastNight(dayKey, stripDay?.slotDate)
                        ? () => livePlannerApi.markMealSkipped({
                          planItemId: stripDay.planItemId,
                          recipeId: null,
                          name: 'No dinner',
                        })
                        : undefined
                    }
                    onNightOff={
                      isPastEmpty &&
                      stripDay?.planItemId &&
                      stripDay?.status !== 'skipped' &&
                      promptPastNight(dayKey, stripDay?.slotDate)
                        ? (typeId) => livePlannerApi.setNightOff?.(
                          { planItemId: stripDay.planItemId },
                          typeId,
                        )
                        : undefined
                    }
                  />
                );
              }
              return (
                <MealRow
                  key={m.id}
                  meal={m}
                  state="assigned"
                  isToday={todayKey != null && m.assignedTo === todayKey}
                  isHighlighted={mealIsHighlighted(m)}
                  onUnassign={() => unassign(m.id)}
                  onMove={() => { setAddDayKey(null); setPlanSheetOpen(false); setMoveMeal(m); }}
                  onSwap={() => openSwapDinner(m)}
                  dayDates={rowDayDates}
                  onViewRecipe={!onboardingMode && onViewRecipe && m.recipeId ? () => onViewRecipe(m.recipeId) : undefined}
                  onMarkCooked={
                    m.isPast && m.recipeId && promptPastNight(m.assignedTo, m.slotDate)
                      ? () => livePlannerApi.markMealCooked(m)
                      : undefined
                  }
                  onMarkSkipped={
                    m.isPast && m.recipeId && promptPastNight(m.assignedTo, m.slotDate)
                      ? () => livePlannerApi.markMealSkipped(m)
                      : undefined
                  }
                  onNightOff={
                    m.isPast && m.recipeId && promptPastNight(m.assignedTo, m.slotDate)
                      ? (typeId) => livePlannerApi.setNightOff?.(m, typeId)
                      : undefined
                  }
                />
              );
                })}
                </div>
              </div>
            )}

            {packsMode === 'hero' && (
              <RecipePackShelf
                mode="hero"
                packs={starterPacks}
                addedPackIds={addedPackIds}
                onApplyPack={onApplyStarterPack}
                onBrowseAllPacks={onImportRecipe ?? undefined}
              />
            )}

            {packsMode === 'shelf' && (
              <RecipePackShelf
                mode="shelf"
                packs={starterPacks}
                addedPackIds={addedPackIds}
                onApplyPack={onApplyStarterPack}
                onBrowseAllPacks={onImportRecipe ?? undefined}
              />
            )}
          </div>
        </div>

        {!isCold && !onboardingMode && (
          <CompactGroceryRail
            assigned={assigned}
            filter={groceryFilter}
            onFilter={onGroceryFilter}
            onBuildGroceryList={(opts) => {
              livePlannerApi.buildGroceryList(opts).catch(() => {
                // Toast is shown inside the hook; swallow so React doesn't see an unhandled rejection.
              });
            }}
            buildBusy={livePlannerApi.busy}
            allIngredientsLoading={assignedIngredientsLoading}
            allIngredientsError={assignedIngredientsError}
            onRetryAllIngredients={retryAssignedIngredientLines}
          />
        )}
      </div>
          <SwapRecipeSheet
            open={Boolean(swapMeal) || Boolean(addDayKey)}
            mode={swapMeal ? 'swap' : 'add'}
            meal={swapMeal}
            dayKey={addDayKey}
            candidates={livePlannerApi.suggestionMeals}
            plannerState={plannerState}
            dietary={dietary}
            libraryCount={libraryCount}
            onImportRecipe={onImportRecipe}
            highlightRecipeId={highlightRecipeId}
            onRemovePick={removePick}
            removePickBusy={livePlannerApi.busy}
            density="desktop"
            onSaveNightOff={onboardingMode ? undefined : async (dayKey, typeId) => {
              const ok = await livePlannerApi.setNightOff?.(dayKey, typeId)
              if (ok) closeRecipePicker()
            }}
            onClearNight={
              onboardingMode || swapMeal?.status !== 'night_off'
                ? undefined
                : () => {
                    unassign(swapMeal.id)
                    closeRecipePicker()
                  }
            }
            onClose={closeRecipePicker}
            onPick={async (recipe) => {
              if (!recipe?.recipeId) return
              const adding = !swapMeal && Boolean(addDayKey)
              const dinnersBefore = dinnersOnWeek()
              let assigned = false
              if (swapMeal) {
                await livePlannerApi.swapRecipeInPlace?.(swapMeal, recipe.recipeId)
              } else if (addDayKey) {
                assigned = Boolean(await livePlannerApi.assignToDay?.(recipe, addDayKey))
              }
              closeRecipePicker()
              if (adding && assigned) promptSaveIfSecondDinner(dinnersBefore + 1)
            }}
          />
          <PlanWithCookSheet
            open={planSheetOpen}
            initialDraft={planSeed}
            openDayKeys={planDayKeys}
            previewRecipes={livePlannerApi.suggestionMeals || []}
            onboardingMode={onboardingMode}
            density="desktop"
            onClose={closePlanSheet}
            onSubmit={submitPlan}
          />
      </div>

      <MobileMoveMealSheet
        open={Boolean(moveMeal)}
        meal={moveMeal}
        stripDays={stripDays}
        mealWeekStart={livePlannerApi.weekStartDate}
        busy={livePlannerApi.busy}
        onClose={() => setMoveMeal(null)}
        onSelectDay={async (day) => {
          if (!moveMeal) return
          const moving = moveMeal
          setMoveMeal(null)
          await livePlannerApi.assignToStripDay(moving, day)
        }}
      />

      <MoveMealConflictModal
        open={Boolean(livePlannerApi.pendingMoveConflict)}
        movingMeal={livePlannerApi.pendingMoveConflict?.movingMeal}
        stripDay={livePlannerApi.pendingMoveConflict?.stripDay}
        fromWeekStart={livePlannerApi.pendingMoveConflict?.fromWeekStart}
        busy={livePlannerApi.busy}
        onCancel={() => livePlannerApi.cancelMoveConflict?.()}
        onReplace={() => void livePlannerApi.resolveMoveConflict?.('replace')}
        onSwap={() => void livePlannerApi.resolveMoveConflict?.('swap')}
      />
    </div>
  );
}

function PlannerNotice({ notice, onClear, onAction }) {
  if (!notice) return null;
  return (
    <StatusPill variant={notice.kind || 'info'} onDismiss={onClear}>
      <span className="planner-notice-text">{notice.text}</span>
      {notice.actionLabel && onAction ? (
        <button
          type="button"
          className="planner-notice-action"
          onClick={(e) => {
            e.stopPropagation()
            onAction()
          }}
        >
          {notice.actionLabel}
        </button>
      ) : null}
    </StatusPill>
  );
}

function renderBannerBody(banner) {
  if (!banner?.bodyStrong || typeof banner.body !== 'string') return banner?.body;

  const index = banner.body.indexOf(banner.bodyStrong);
  if (index === -1) return banner.body;

  return (
    <>
      {banner.body.slice(0, index)}
      <b>{banner.bodyStrong}</b>
      {banner.body.slice(index + banner.bodyStrong.length)}
    </>
  );
}

function PlannerStateBanner({ banner, onDismiss }) {
  return (
    <StateBanner
      variant={banner.tone}
      eyebrow={banner.eyebrow}
      headline={banner.title}
      body={renderBannerBody(banner)}
      dismissible={banner.tone === 'ai' && !!onDismiss}
      onDismiss={onDismiss}
      className="planner-state-banner"
    />
  );
}

function PlannerEmptyHero({
  libraryCount = null,
  onSwitchToRecipes = null,
  onImportRecipe = null,
}) {
  const liveLibrary = typeof libraryCount === 'number' ? libraryCount : null;
  const needMore = liveLibrary != null ? Math.max(8 - liveLibrary, 3) : null;

  const liveCopy = liveLibrary == null
    ? null
    : (liveLibrary === 0
        ? {
            eyebrow: 'Almost ready to plan',
            title: 'Add a few recipes to get started',
            body: `Import about ${needMore} recipes you already cook, then add them to open nights.`,
          }
        : {
            eyebrow: 'Almost ready to plan',
            title: `${liveLibrary === 1 ? "You've got 1 recipe" : `You've got ${liveLibrary} recipes`} — add a few more`,
            body: `Add about ${needMore} more so we can plan a varied week without repeating the same dinner.`,
          });

  const display = liveCopy || COLD_START_PLANNER_HERO;

  return (
    <SharedEmptyHero
      className="planner-empty-state-hero"
      eyebrow={display.eyebrow}
      headline={display.title}
      body={display.body}
      actions={[
        { label: 'Import a recipe', kind: 'primary', onClick: onImportRecipe },
        { label: 'Browse recipes', kind: 'secondary', onClick: onSwitchToRecipes },
      ]}
    />
  );
}
